/**
 * ============================================================================
 * StickyWidget — Controller (client)
 * ============================================================================
 * Owns LCP-deferred visibility, exit-intent / inactivity timers, and Apply
 * CTA modal open. Handlers are useCallback-stable so the view does not
 * re-subscribe unnecessarily. Behavior preserved from pre-MVC widget.
 * ============================================================================
 */
"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { IModalActions, ModalTypes } from "@/types/modal";
import { useModalDispatch } from "@/context/ModalContext";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue, updateStateSession } from "@/context/utils";
import { EXCLUDED_PATHS, precedentTimer } from "../utils";
import { StorageService } from "@/helpers/storage.service";
import { loadCryptoHelpers } from "@/components/molecules/CryptoJs/lazyHelpers";
import {
  disableVideoControls,
  is30SecModalBlocked,
} from "@/data/permissionUtils";
import { FREEZE_RFIS } from "@/components/organisms/HeaderOpt/utils";
import type {
  StickyWidgetProps,
  StickyWidgetViewModel,
} from "../model/stickyWidget.types";

export function useStickyWidgetController(
  props: StickyWidgetProps,
): StickyWidgetViewModel | null {
  const {
    associatedPrograms = [],
    stickyWidget,
    modalThirtySecondImage,
  } = props;

  const [isVisible, setIsVisible] = useState(false);
  const modalDispatch = useModalDispatch();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const amsType = searchParams?.get("AMSType");

  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Latest props for empty-deps effects / timer callbacks (avoids stale
  // closures without re-binding document listeners on every render).
  const associatedProgramsRef = useRef(associatedPrograms);
  const modalThirtySecondImageRef = useRef(modalThirtySecondImage);
  const pathnameRef = useRef(pathname);
  associatedProgramsRef.current = associatedPrograms;
  modalThirtySecondImageRef.current = modalThirtySecondImage;
  pathnameRef.current = pathname;

  const inactivityCleanupRef = useRef<(() => void) | null>(null);
  const startInactivityTimerRef = useRef<() => void>(() => {});
  const openModalAfterInactivityRef = useRef<() => Promise<void>>(
    async () => {},
  );

  openModalAfterInactivityRef.current = async () => {
    await loadCryptoHelpers();
    const path = window.location.pathname;
    if (is30SecModalBlocked(path)) {
      inactivityCleanupRef.current?.();
      if ((window as any)?.commonModalTimerCleanup) {
        (window as any).commonModalTimerCleanup();
      }
      return;
    }
    disableVideoControls();
    const modalElement = document.querySelector("div#_modal");
    const is30SecModalPopped = StorageService.get("is30SecModalPopped");
    const tempisSessionModalActive = StorageService.get("timeConnectModal");
    const isChatBotOpen = StorageService.get("chatbot-open", "local") == "true";

    if (
      !modalElement.childElementCount &&
      (!tempisSessionModalActive || !is30SecModalPopped)
    ) {
      if (FREEZE_RFIS.includes(window.location.pathname) || isChatBotOpen) {
        inactivityCleanupRef.current?.();
        return;
      }
      modalDispatch({
        type: IModalActions.UpdateModal,
        data: {
          modalType: ModalTypes.ConnectModal30Sec,
          isModalOpen: true,
          modalData: {
            associatedPrograms: associatedProgramsRef.current,
            exitIntentImage: modalThirtySecondImageRef.current,
          },
        },
      });
      StorageService.set("timeConnectModal", true);
      StorageService.set("is30SecModalPopped", true);
    }

    inactivityCleanupRef.current?.();
    if ((window as any)?.commonModalTimerCleanup) {
      (window as any).commonModalTimerCleanup();
    }
  };

  startInactivityTimerRef.current = () => {
    const path = pathnameRef.current || window.location.pathname;
    if (is30SecModalBlocked(path)) {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityCleanupRef.current?.();
      if ((window as any)?.commonModalTimerCleanup) {
        (window as any).commonModalTimerCleanup();
      }
      return;
    }
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if ((window as any)?.commonModalTimerCleanup) {
      (window as any).commonModalTimerCleanup();
    }

    const resetTimer = () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if ((window as any)?.commonModalTimerCleanup) {
        (window as any).commonModalTimerCleanup();
      }
      inactivityTimerRef.current = setTimeout(
        () => openModalAfterInactivityRef.current(),
        precedentTimer(pathnameRef.current),
      );
    };

    const events = ["keydown"];
    events.forEach((event) =>
      window.addEventListener(event, resetTimer, { passive: true }),
    );
    inactivityTimerRef.current = setTimeout(
      () => openModalAfterInactivityRef.current(),
      precedentTimer(pathnameRef.current),
    );

    const cleanup = () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      events.forEach((event) =>
        window.removeEventListener(event, resetTimer),
      );
    };

    (window as any).sideButtonsTimerCleanup = cleanup;
    inactivityCleanupRef.current = cleanup;
  };

  useEffect(() => {
    void loadCryptoHelpers();
    if (sessionStorage.getItem("stickyWidgetClosed") == "true") {
      setIsVisible(false);
      return;
    }

    // Show after LCP so sticky chrome doesn't compete with FCP/LCP/SI on mobile
    let cancelled = false;
    let fallbackId: ReturnType<typeof setTimeout> | undefined;
    let idleId: number | undefined;
    let observer: PerformanceObserver | undefined;

    const clearFallback = () => {
      if (fallbackId !== undefined) {
        clearTimeout(fallbackId);
        fallbackId = undefined;
      }
    };

    const show = () => {
      if (cancelled || sessionStorage.getItem("stickyWidgetClosed") === "true")
        return;
      setIsVisible(true);
    };

    const scheduleShow = () => {
      if (cancelled || sessionStorage.getItem("stickyWidgetClosed") === "true")
        return;
      if ("requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(() => show(), { timeout: 2000 });
      } else {
        fallbackId = setTimeout(show, 300);
      }
    };

    try {
      const existing = performance.getEntriesByType("largest-contentful-paint");
      if (existing.length > 0) {
        scheduleShow();
      } else if ("PerformanceObserver" in window) {
        observer = new PerformanceObserver(() => {
          observer?.disconnect();
          clearFallback();
          scheduleShow();
        });
        observer.observe({ type: "largest-contentful-paint", buffered: true });
        fallbackId = setTimeout(() => {
          observer?.disconnect();
          scheduleShow();
        }, 1000);
      } else {
        fallbackId = setTimeout(show, 1500);
      }
    } catch {
      fallbackId = setTimeout(show, 1500);
    }

    return () => {
      cancelled = true;
      observer?.disconnect();
      clearFallback();
      if (idleId !== undefined && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("exitPopupShown") === "true") return;

    const handleExitIntent = async (e: MouseEvent) => {
      if (e.clientY > 0 || amsType === "Jiopc") return;

      const path = window.location.pathname;
      if (is30SecModalBlocked(path)) return;

      await loadCryptoHelpers();
      const modalElement = document.querySelector("div#_modal");
      const isChatBotOpen = StorageService.get("chatbot-open", "local") == "true";

      if (!modalElement?.childElementCount && !isChatBotOpen) {
        if (FREEZE_RFIS.includes(window.location.pathname)) return;
        modalDispatch({
          type: IModalActions.UpdateModal,
          data: {
            modalType: ModalTypes.ConnectModal30Sec,
            isModalOpen: true,
            modalData: {
              associatedPrograms: associatedProgramsRef.current,
              metaApiParams: {
                utm_content: "Popup",
              },
              isExitIntent: true,
              exitIntentImage: modalThirtySecondImageRef.current,
            },
          },
        });
        sessionStorage.setItem("exitPopupShown", "true");
        StorageService.set("timeConnectModal", true);
        StorageService.set("is30SecModalPopped", true);
        document.removeEventListener("mouseleave", handleExitIntent);
      }
    };

    document.addEventListener("mouseleave", handleExitIntent);
    return () => document.removeEventListener("mouseleave", handleExitIntent);
  }, [modalDispatch]);

  const close = useCallback(() => {
    sessionStorage.setItem("stickyWidgetClosed", "true");
    setIsVisible(false);
    startInactivityTimerRef.current();
  }, []);

  const onClickhandler = useCallback(() => {
    const programs = associatedProgramsRef.current;
    const path = pathnameRef.current;

    if (programs.length == 1) {
      modalDispatch({
        type: IModalActions.OpenModal,
        data: {
          modalType: ModalTypes.ConnectModalMba,
          modalData: {
            associatedPrograms: programs,
            metaApiParams: {
              utm_content: "Widget",
            },
            rfiCase: path.includes("lp-nw")
              ? "THANK_YOU_PAGE_CASE"
              : "REDIRECT_APPLICATION_JOURNEY",
            isLambdaAPI: path.includes("lp-nw") ? true : false,
          },
        },
      });
    } else {
      modalDispatch({
        type: IModalActions.OpenModal,
        data: {
          modalType: ModalTypes.ConnectModalV2,
          modalData: {
            associatedPrograms: programs,
            isLandingPage: true,
            metaApiParams: {
              utm_content: "Widget",
            },
            rfiCase: path.includes("lp-nw")
              ? "REDIRECT_TO_THANKYOU"
              : "REDIRECT_APPLICATION_JOURNEY",
            heading: "Join India's No. 1 Online University",
            submitBtnText: "Apply Now",
          },
        },
      });
    }

    setTimeout(() => {
      pushToDataLayer({
        event: "lead_initiate",
        cta_text: "Register for AONSAT now.",
        section_name: "Sticky Section",
        page_type: getGlobalStateValue("pageType"),
        page_url: window?.location?.href || "NA",
      });

      updateStateSession({
        parentSection: "Sticky Section",
        leadStartingCta: "Register for AONSAT now.",
      });
    }, 2000);

    sessionStorage.setItem("stickyWidgetClosed", "true");
    setIsVisible(false);
    startInactivityTimerRef.current();
  }, [modalDispatch]);

  if (
    EXCLUDED_PATHS.some(
      (p) => pathname === p || pathname?.startsWith(p + "/"),
    )
  ) {
    return null;
  }

  if(amsType === "Jiopc") return null;

  return {
    isVisible,
    stickyWidget,
    close,
    onClickhandler,
  };
}
